import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapPin, Heart, Calendar, Sparkles, Map } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import BurgerMenu from './BurgerMenu';
import BackButton from './BackButton';
import tourService from '../services/tourService';
import { useDistanceUnit } from '../contexts/DistanceUnitContext';
import { useAuth } from '../contexts/AuthContext';
import SwipeableTourCard from './SwipeableTourCard';
import { useLocationCoords } from '../contexts/LocationContext';
import { useTheme } from '../contexts/ThemeContext';

// Animated dots component for "Researching..."
const AnimatedDots: React.FC = () => {
  const [dots, setDots] = useState('.');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev === '.') return '..';
        if (prev === '..') return '...';
        return '.';
      });
    }, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  return <span>{dots}</span>;
};

interface TourSummary {
  tourId: string;
  userId?: string;
  title: string;
  description: string;
  streetName: string;
  city: string;
  country: string;
  formattedAddress?: string;
  estimatedDuration: number;
  distance: string;
  audioStatus: string;
  researchStatus?: string;
  createdAt: string;
  viewCount: number;
  saveCount?: number;
  distanceFromUser?: number; // in meters
  distanceFromUserFormatted?: string;
  isSavedFromOthers?: boolean; // Flag to track if this is a saved tour from another user
}

const PastTours: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [tours, setTours] = useState<TourSummary[]>([]);
  const [allTours, setAllTours] = useState<TourSummary[]>([]); // Store complete list
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalTours, setTotalTours] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const { formatDistance } = useDistanceUnit();
  const { latitude, longitude } = useLocationCoords();

  // Fetch tours when component mounts or when navigating back to this page
  useEffect(() => {
    fetchTours(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const fetchTours = async (isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      // Fetch both user-created tours AND saved tours
      const [createdToursResponse, savedToursResponse] = await Promise.all([
        // Tours created by the user
        tourService.listTours(50, 0, {
          userId: user?.id,
          latitude,
          longitude,
          sortByDistance: false
        }),
        // Tours saved by the user
        tourService.getSavedTours()
      ]);
      
      // Combine both lists and remove duplicates
      const createdTours = createdToursResponse.tours || [];
      const savedTours = savedToursResponse.tours || [];
      
      // Create a map to avoid duplicates (user might save their own tour)
      const tourMap: { [key: string]: TourSummary } = {};
      
      // Add created tours first (mark as NOT saved from others)
      createdTours.forEach((tour: TourSummary) => {
        tourMap[tour.tourId] = { ...tour, isSavedFromOthers: false };
      });
      
      // Add saved tours (won't overwrite created tours due to object key)
      // Mark these as saved from others (since they weren't created by user)
      savedTours.forEach((tour: TourSummary) => {
        if (!tourMap[tour.tourId]) {
          tourMap[tour.tourId] = { ...tour, isSavedFromOthers: true };
        }
      });
      
      // Convert map to array and sort by creation date (newest first)
      const completeTourList: TourSummary[] = Object.values(tourMap).sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      
      // Store the complete list
      setAllTours(completeTourList);
      
      // Handle pagination - initial load shows first 10
      const initialDisplayCount = Math.min(10, completeTourList.length);
      const displayedTours = completeTourList.slice(0, initialDisplayCount);
      
      setTours(displayedTours);
      setTotalTours(completeTourList.length);
      setHasMore(initialDisplayCount < completeTourList.length);
    } catch (err) {
      setError('Failed to load tours. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreTours = useCallback(() => {
    if (loadingMore || !hasMore) return;
    
    // Load next batch from the stored allTours array
    const currentLength = tours.length;
    const nextBatch = allTours.slice(currentLength, currentLength + 10);
    
    if (nextBatch.length > 0) {
      setTours((prev) => [...prev, ...nextBatch]);
      setHasMore(currentLength + nextBatch.length < allTours.length);
    }
  }, [loadingMore, hasMore, tours.length, allTours]);

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    
    // Trigger loading when within 300px of bottom
    if (scrollHeight - scrollTop <= clientHeight + 300 && !loadingMore && hasMore) {
      loadMoreTours();
    }
  }, [loadingMore, hasMore, loadMoreTours]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: '2-digit' 
    });
  };

  const formatLocation = (tour: TourSummary) => {
    return tour.formattedAddress || 
           `${tour.streetName || ''}${tour.city ? ', ' + tour.city : ''}${tour.country ? ', ' + tour.country : ''}` ||
           'Address not available';
  };

  const handleTourClick = (tour: TourSummary) => {
    // If tour is pending, check if there's generation state and navigate back to generating screen
    if (tour.researchStatus === 'pending') {
      const storedPendingTour = localStorage.getItem('pendingTourGeneration');
      
      if (storedPendingTour) {
        const pendingData = JSON.parse(storedPendingTour);
        
        if (pendingData.tourId === tour.tourId) {
          // Navigate back to generating screen
          console.log('✅ Match found! Navigating to /generating');
          navigate('/generating', {
            state: {
              coordinates: pendingData.coordinates,
              interests: pendingData.interests,
              geocodeData: pendingData.geocodeData,
              selectedPlaces: pendingData.selectedPlaces,
              nearbyPlaces: pendingData.nearbyPlaces
            }
          });
          return;
        } else {
          console.log('❌ Tour IDs do not match');
        }
      } else {
        console.log('❌ No stored pending tour found in localStorage');
      }
      
      // If no matching generation state found, just refresh to check if it's completed
      console.log('🔄 Refreshing tours to check if completed');
      fetchTours(true);
      return;
    }
    
    // Normal tour - navigate to tour page
    console.log('➡️ Normal tour - navigating to /tour/' + tour.tourId);
    navigate(`/tour/${tour.tourId}`);
  };

  const handleShare = async (tour: TourSummary) => {
    const shareUrl = `${window.location.origin}/tour/${tour.tourId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: tour.title,
          text: `Check out this tour: ${tour.title}`,
          url: shareUrl,
        });
      } catch (err) {
        // Ignore abort errors
        if ((err as Error).name !== 'AbortError') {
          console.error('Error sharing:', err);
        }
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Tour link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleDelete = async (tourId: string) => {
    // Find the tour to check if it's saved from others or created by user
    const tour = tours.find(t => t.tourId === tourId);
    
    if (!tour) {
      console.error('Tour not found');
      return;
    }
    
    const isSavedFromOthers = tour.isSavedFromOthers;
    
    // Different messages for own tours vs saved tours
    const confirmMessage = isSavedFromOthers
      ? 'Remove this tour from your collection?'
      : 'Are you sure you want to delete this tour? This action cannot be undone.';
    
    if (window.confirm(confirmMessage)) {
      try {
        // Optimistic update
        setTours(prev => prev.filter(t => t.tourId !== tourId));
        setAllTours(prev => prev.filter(t => t.tourId !== tourId));
        setTotalTours(prev => prev - 1);
        
        if (isSavedFromOthers) {
          // Unsave the tour (user saved it from someone else)
          const result = await tourService.unsaveTour(tourId);
          if (!result.success) {
            throw new Error(result.error || 'Failed to unsave tour');
          }
        } else {
          // Delete the tour (user created it)
          await tourService.deleteTour(tourId);
        }
      } catch (err) {
        console.error('Failed to delete/unsave tour:', err);
        alert(`Failed to remove tour: ${err instanceof Error ? err.message : 'Unknown error'}`);
        // Revert optimistic update on error
        fetchTours(true);
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="app">
        <div className="header">
          <BackButton onClick={() => navigate('/home')} />
          <h2 className="header-title">My Tour Collection</h2>
          <BurgerMenu />
        </div>
        <div className="container flex-center" style={{ height: '50vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
            <p style={{ color: 'var(--text-secondary)' }}>Loading your tours...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="app">
        <div className="header">
          <BackButton onClick={() => navigate('/home')} />
          <h2 className="header-title">My Tour Collection</h2>
          <BurgerMenu />
        </div>
        <div className="container">
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center'
          }}>
            <p style={{ color: 'var(--error)', marginBottom: '16px' }}>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="button button-primary"
              style={{ padding: '12px 24px' }}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (tours.length === 0) {
    return (
      <div className="app">
        <div className="header">
          <BackButton onClick={() => navigate('/home')} />
          <h2 className="header-title">My Tour Collection</h2>
          <BurgerMenu />
        </div>
        <div className="container" style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - 80px)', // Subtract header height
          overflow: 'hidden'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'var(--text-secondary)',
            marginTop: '-80px' // Move content higher
          }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No saved tours</p>
            <p style={{ fontSize: '14px', marginBottom: '24px' }}>Tours you save go here!</p>
            <motion.button
              onClick={() => navigate('/location')}
              whileTap={{ 
                backgroundColor: 'rgba(67, 56, 202, 1)',
                transition: { duration: 0 }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                backgroundColor: '#4F46E5',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <Sparkles size={16} />
              Create Your First Tour
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>My Tours | Miaitor</title>
        <meta name="title" content="My Tours | Miaitor" />
        <meta name="description" content="View and manage your created tours." />
        <meta name="robots" content="noindex, nofollow" /> {/* Private page, don't index */}
        
        <link rel="canonical" href="https://miaitor.com/past-tours" />
      </Helmet>
    
      <div className="app">
        <div className="header">
          <BackButton onClick={() => navigate('/')} />
        <h2 className="header-title">My Tour Collection</h2>
        <BurgerMenu />
      </div>

      <div className="container">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* My Tours Map Button */}
          <motion.button
            onClick={() => navigate('/my-tours-map')}
            whileTap={{ 
              scale: 0.98,
              transition: { duration: 0.1 }
            }}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '14px 20px',
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '20px',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
              WebkitTapHighlightColor: 'transparent',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#4F46E5';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--primary-color)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.3)';
            }}
          >
            <Map size={20} />
            My Tours Map
          </motion.button>
          
          <AnimatePresence mode="popLayout">
            {tours.map((tour, index) => {
              // Add cascading delay for initial cards (first 5), new cards should animate immediately
              const isInitialCard = index < 5;
              const animationDelay = isInitialCard ? index * 0.1 : 0;
              
              return (
                <motion.div
                  key={tour.tourId}
                  initial={{ opacity: 0 }}
                  animate={{ 
                    opacity: 1,
                  }}
                  exit={{ opacity: 0, height: 0, marginBottom: 0, transition: { duration: 0.2 } }}
                  transition={{ 
                    opacity: { duration: 0.4, delay: animationDelay },
                  }}
                  style={{
                    marginBottom: index === tours.length - 1 && !loadingMore ? '0' : '16px',
                  }}
                >
                  <SwipeableTourCard
                    onShare={() => handleShare(tour)}
                    onDelete={() => handleDelete(tour.tourId)}
                    onClick={() => handleTourClick(tour)}
                    style={{
                      opacity: tour.researchStatus === 'pending' ? 0.85 : 1,
                      border: tour.researchStatus === 'pending' ? '2px solid var(--primary-color)' : undefined,
                    }}
                  >
                    <div style={{ padding: '16px', position: 'relative' }}>
                      <div style={{ marginBottom: '8px', pointerEvents: 'none' }}>
                        <h3 style={{ fontSize: '18px', marginBottom: '4px', color: 'var(--text-primary)' }}>
                          {tour.researchStatus === 'pending' ? (
                            <>Researching<AnimatedDots /></>
                          ) : (
                            tour.title
                          )}
                        </h3>
                      </div>
                      
                      {/* Pulsing indicator for pending tours */}
                      {tour.researchStatus === 'pending' && (
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          style={{
                            position: 'absolute',
                            right: '16px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--primary-color)',
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      
                      <p style={{ 
                        fontSize: '14px', 
                        color: 'var(--text-secondary)',
                        marginBottom: '12px',
                        lineHeight: '1.4',
                        pointerEvents: 'none'
                      }}>
                        {formatLocation(tour)}
                      </p>
                      
                      <div style={{ display: 'flex', gap: '16px', color: 'var(--text-secondary)', flexWrap: 'wrap', pointerEvents: 'none' }}>
                        {tour.distanceFromUser !== undefined && (
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <MapPin size={16} />
                            <span style={{ fontSize: '14px' }}>{formatDistance(tour.distanceFromUser)}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <Calendar size={16} />
                          <span style={{ fontSize: '14px' }}>{formatDate(tour.createdAt)}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <Heart size={16} />
                          <span style={{ fontSize: '14px' }}>{(tour.saveCount || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </SwipeableTourCard>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {/* Loading more skeleton */}
          {loadingMore && (
            <>
              {[1, 2, 3].map((skeletonIndex) => (
                <motion.div
                  key={`skeleton-more-${skeletonIndex}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, delay: skeletonIndex * 0.05 }}
                  style={{
                    padding: '16px',
                    backgroundColor: 'var(--card-background)',
                    borderRadius: '16px',
                    marginBottom: '16px',
                    boxShadow: 'var(--shadow-sm)'
                  }}
                >
                  {/* Skeleton title */}
                  <div style={{
                    height: '20px',
                    backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    width: '70%',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }} />
                  
                  {/* Skeleton description */}
                  <div style={{
                    height: '14px',
                    backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                    borderRadius: '4px',
                    marginBottom: '12px',
                    width: '50%',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    animationDelay: '0.2s'
                  }} />
                  
                  {/* Skeleton metadata */}
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{
                      height: '14px',
                      backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                      borderRadius: '4px',
                      width: '60px',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: '0.4s'
                    }} />
                    <div style={{
                      height: '14px',
                      backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                      borderRadius: '4px',
                      width: '50px',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: '0.6s'
                    }} />
                  </div>
                </motion.div>
              ))}
            </>
          )}

          {!loading && !loadingMore && !hasMore && tours.length > 0 && (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: 'var(--text-secondary)',
              fontSize: '14px'
            }}>
              You've reached the end of your collection
            </div>
          )}
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default PastTours;

